// frontend/src/components/Payment/SumUpPaymentButton.js
import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  VStack,
  HStack,
  Icon,
  useToast,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box
} from '@chakra-ui/react';
import { FaCreditCard, FaCheckCircle } from 'react-icons/fa';

const SumUpPaymentButton = ({ 
  contributionId, 
  amount, 
  contributionType,
  onSuccess,
  onError,
  buttonText = "Payer avec SumUp",
  variant = "solid",
  colorScheme = "blue",
  size = "md"
}) => {
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Vous devez être connecté pour effectuer un paiement');
      }

      const response = await fetch('/api/payments/sumup/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contribution_id: contributionId,
          return_url: `${window.location.origin}/contributions/payment-return`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la création du paiement');
      }

      if (data.success && data.checkout_url) {
        // Sauvegarder les informations du paiement en cours
        sessionStorage.setItem('pending_sumup_payment', JSON.stringify({
          checkout_id: data.checkout_id,
          contribution_id: contributionId,
          amount: amount,
          timestamp: Date.now()
        }));

        // Rediriger vers la page de paiement SumUp
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      setLoading(false);
      
      toast({
        title: 'Erreur de paiement',
        description: error.message || 'Impossible de créer le paiement. Veuillez réessayer.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });

      if (onError) {
        onError(error);
      }
    }
  };

  const handleConfirmPayment = () => {
    onClose();
    handlePayment();
  };

  return (
    <>
      <Button
        leftIcon={<FaCreditCard />}
        colorScheme={colorScheme}
        variant={variant}
        size={size}
        onClick={onOpen}
        isLoading={loading}
        loadingText="Redirection..."
      >
        {buttonText}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmer le paiement SumUp</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Paiement sécurisé</AlertTitle>
                  <AlertDescription fontSize="sm">
                    Vous allez être redirigé vers la page de paiement sécurisée de SumUp
                  </AlertDescription>
                </Box>
              </Alert>

              <VStack spacing={2} align="stretch" p={4} bg="gray.50" borderRadius="md">
                <HStack justify="space-between">
                  <Text fontWeight="medium">Type de cotisation :</Text>
                  <Text>{contributionType}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="medium">Montant à payer :</Text>
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">
                    {formatAmount(amount)}
                  </Text>
                </HStack>
              </VStack>

              <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
                <HStack>
                  <Icon as={FaCheckCircle} color="green.500" />
                  <Text>Paiement 100% sécurisé</Text>
                </HStack>
                <HStack>
                  <Icon as={FaCheckCircle} color="green.500" />
                  <Text>Confirmation par email</Text>
                </HStack>
                <HStack>
                  <Icon as={FaCheckCircle} color="green.500" />
                  <Text>Support de plusieurs méthodes de paiement</Text>
                </HStack>
              </VStack>

              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Text>
                  Après le paiement, vous serez automatiquement redirigé vers cette page.
                  Ne fermez pas la fenêtre avant la fin du processus.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmPayment}
              isLoading={loading}
              loadingText="Redirection..."
              leftIcon={<FaCreditCard />}
            >
              Continuer vers SumUp
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SumUpPaymentButton;
